// pages/GuildList.js (UI 색상 수정된 버전)
import React, { useState, useEffect } from "react";
import { guildDataService } from "./GuildDataService";
// 기존 import에 추가
import GuildCard from "./GuildCard";
import GuildHashtags from "./GuildHashtags";

const SPORTS = ["전체", "탁구", "배드민턴", "테니스", "볼링", "축구", "농구"];

function GuildList({ guilds, currentUser, onBack, onMessage, searchType, setSearchType, searchQuery, setSearchQuery }) {
  const [filteredGuilds, setFilteredGuilds] = useState([]);
  const [selectedSport, setSelectedSport] = useState("전체");
  const [sortBy, setSortBy] = useState("latest"); // latest, members, points
  const [loading, setLoading] = useState(false);

  // 길드 가입 여부 확인
  const isJoined = !!currentUser.guildId;

  useEffect(() => {
    filterAndSortGuilds();
  }, [guilds, searchQuery, selectedSport, sortBy]);

  const filterAndSortGuilds = () => {
    let filtered = [...guilds];

    // 검색어 필터링
    if (searchQuery && searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      if (searchType === "name") {
        filtered = filtered.filter(guild => guild.name && guild.name.toLowerCase().includes(q));
      }
      if (searchType === "hashtag") {
        filtered = filtered.filter(guild => Array.isArray(guild.hashtags) && guild.hashtags.some(tag => tag.toLowerCase().replace('#','').includes(q.replace('#',''))));
      }
      if (searchType === "leader") {
        filtered = filtered.filter(guild => guild.leaderName && guild.leaderName.toLowerCase().includes(q));
      }
    }

    // 스포츠 필터링
    if (selectedSport !== "전체") {
      filtered = filtered.filter(guild => guild.sport === selectedSport);
    }

    // 정렬
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "members":
          return (b.memberCount || 0) - (a.memberCount || 0);
        case "points":
          return (b.point || 0) - (a.point || 0);
        case "name":
          return a.name.localeCompare(b.name);
        case "latest":
        default:
          const aTime = a.createdAt?.toDate?.() || new Date(0);
          const bTime = b.createdAt?.toDate?.() || new Date(0);
          return bTime - aTime;
      }
    });

    setFilteredGuilds(filtered);
  };

  const handleApplyGuild = async (guild) => {
    if (!currentUser) {
      onMessage("로그인이 필요합니다.");
      return;
    }

    try {
      setLoading(true);
      await guildDataService.applyToGuild(
        currentUser.id,
        currentUser.nickname || currentUser.id,
        guild
      );
      onMessage("가입 신청이 전송되었습니다.");
    } catch (error) {
      console.error("길드 가입 신청 실패:", error);
      if (error.message && error.message.includes("이미 가입 신청이 진행 중")) {
        onMessage("이미 해당 길드에 가입 신청이 진행 중입니다.");
      } else if (error.message && error.message.includes("이미 해당 길드에 가입되어 있습니다")) {
        onMessage("이미 해당 길드에 가입되어 있습니다.");
      } else {
        onMessage("가입 신청에 실패했습니다.");
      }
    } finally {
      setLoading(false);
    }
  };

  const getSportEmoji = (sport) => {
    const emojis = {
      "탁구": "🏓",
      "배드민턴": "🏸", 
      "테니스": "🎾",
      "볼링": "🎳",
      "축구": "⚽",
      "농구": "🏀"
    };
    return emojis[sport] || "🏃";
  };

  const getSortLabel = (sort) => {
    const labels = {
      "latest": "최신순",
      "members": "멤버순",
      "points": "포인트순",
      "name": "이름순"
    };
    return labels[sort] || "최신순";
  };

  return (
    <div style={styles.guildListContainer} className="fade-in">
      <div style={styles.guildListHeader}>
        <div style={styles.headerLeft}>
          <h2 style={styles.guildListTitle}>🔍 활동 중인 길드들</h2>
          <div style={styles.guildCount}>
            총 {filteredGuilds.length}개 길드
          </div>
        </div>
        <button 
          onClick={onBack}
          style={styles.backButton}
        >
          ← 돌아가기
        </button>
      </div>

      {/* 검색바/필터: 리스트 위에만 노출 */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
        <select
          value={searchType}
          onChange={e => setSearchType(e.target.value)}
          style={{
            padding: "10px 16px",
            borderRadius: 8,
            border: "1px solid #e5e7eb",
            fontSize: 15,
            background: "#f9fafb"
          }}
        >
          <option value="name">길드명</option>
          <option value="hashtag">해시태그</option>
          <option value="leader">길드장 이름</option>
        </select>
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder={
            searchType === "name" ? "길드명을 입력하세요" :
            searchType === "hashtag" ? "해시태그를 입력하세요" :
            "길드장 이름을 입력하세요"
          }
          style={{
            flex: 1,
            padding: "10px 16px",
            borderRadius: 8,
            border: "1px solid #e5e7eb",
            fontSize: 15,
            background: "#f9fafb"
          }}
        />
      </div>
      
      {/* 길드 목록 */}
      <div style={styles.guildGrid}>
        {guilds
          .filter(guild => !currentUser.guildId || guild.id !== currentUser.guildId)
          .map((guild, index) => (
            <div key={guild.id} style={styles.guildCard} className="glass-morphism hover-scale">
              <div style={styles.guildCardHeader}>
                <div style={styles.guildCardAvatar}>
                  {guild.cover ? (
                    <img src={guild.cover} alt="길드 로고" style={styles.guildCardImage} />
                  ) : (
                    <div style={styles.guildCardDefaultAvatar}>🏰</div>
                  )}
                </div>
                <div style={styles.guildCardInfo}>
                  <h3 style={styles.guildCardName}>{guild.name}</h3>
                  <div style={styles.guildCardSport}>
                    {getSportEmoji(guild.sport)} {guild.sport}
                  </div>
                  <div style={styles.guildCardLeader}>
                    👑 {guild.leaderNickname || "길드장"}
                  </div>
                </div>
                <div style={styles.guildCardRank}>
                  <div style={styles.rankNumber}>#{index + 1}</div>
                  <div style={styles.rankLabel}>순위</div>
                </div>
              </div>
              
              <div style={styles.guildCardIntro}>
                {guild.intro || "소개가 없습니다"}
              </div>
              {/* 해시태그 표시 */}
              {guild.hashtags && guild.hashtags.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '8px 0 0 0' }}>
                  {guild.hashtags.map((tag, idx) => (
                    <span
                      key={idx}
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: '#fff',
                        borderRadius: 16,
                        padding: '6px 16px',
                        fontSize: 14,
                        fontWeight: 700,
                        letterSpacing: 0.3,
                        boxShadow: '0 2px 8px #667eea33',
                        display: 'inline-block',
                        border: 'none',
                        margin: 0,
                      }}
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
              <div style={styles.guildCardStats}>
                <div style={styles.statItem}>
                  <span style={styles.statIcon}>👥</span>
                  <span style={styles.statValue}>{guild.memberCount || 0}</span>
                  <span style={styles.statLabel}>멤버</span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statIcon}>🏆</span>
                  <span style={styles.statValue}>{guild.point || 0}</span>
                  <span style={styles.statLabel}>포인트</span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statIcon}>📅</span>
                  <span style={styles.statValue}>
                    {guild.createdAt?.toDate ? 
                      guild.createdAt.toDate().toLocaleDateString() : 
                      "미상"}
                  </span>
                  <span style={styles.statLabel}>설립일</span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statIcon}>👑</span>
                  <span style={styles.statValue}>{guild.leaderNickname || guild.leaderName || '길드장'}</span>
                  <span style={styles.statLabel}>길드장</span>
                </div>
                {guild.hashtags && guild.hashtags.length > 0 && (
                  <div style={styles.statItem}>
                    <span style={styles.statIcon}>#️⃣</span>
                    <span style={{ ...styles.statValue, fontSize: 13, fontWeight: 600, color: '#6366f1', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {guild.hashtags.slice(0,2).map(tag => `#${tag}`).join(' ')}
                      {guild.hashtags.length > 2 ? ' ...' : ''}
                    </span>
                    <span style={styles.statLabel}>해시태그</span>
                  </div>
                )}
              </div>

              <div style={styles.guildCardFooter}>
                <div style={styles.guildActivity}>
                  <div style={styles.activityDot}></div>
                  <span>활동 중</span>
                </div>
                
                <button
                  disabled={isJoined}
                  style={{
                    background: isJoined ? '#d1d5db' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 20px',
                    fontWeight: 700,
                    fontSize: 15,
                    cursor: isJoined ? 'not-allowed' : 'pointer',
                    opacity: isJoined ? 0.6 : 1,
                    marginLeft: 12
                  }}
                  onClick={() => {
                    if (isJoined) return;
                    handleApplyGuild(guild);
                  }}
                >
                  {isJoined ? '이미 가입된 상태' : '가입 신청'}
                </button>
              </div>
            </div>
          ))}
      </div>

      {/* 길드 생성 안내 */}
      {filteredGuilds.length > 0 && (
        <div style={styles.createPrompt} className="glass-morphism">
          <div style={styles.promptIcon}>💡</div>
          <div style={styles.promptText}>
            <strong>원하는 길드를 찾지 못하셨나요?</strong>
            <br />직접 길드를 만들어 리더가 되어보세요!
          </div>
          <button 
            onClick={onBack}
            style={styles.createPromptButton}
          >
            길드 만들기
          </button>
        </div>
      )}

      <style>
        {`
          .glass-morphism {
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(20px);
            border: 1px solid rgba(255, 255, 255, 0.3);
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
          }
          
          .hover-scale {
            transition: transform 0.3s ease, box-shadow 0.3s ease;
          }
          
          .hover-scale:hover {
            transform: translateY(-5px);
            box-shadow: 0 16px 64px rgba(0, 0, 0, 0.2);
          }
          
          .fade-in {
            animation: fadeIn 0.5s ease-in;
          }
          
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
    </div>
  );
}

const styles = {
  guildListContainer: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    padding: "20px",
    color: "#1f2937"
  },
  guildListHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 32,
    flexWrap: "wrap",
    gap: 16
  },
  headerLeft: {
    flex: 1
  },
  guildListTitle: {
    fontSize: 32,
    fontWeight: 900,
    margin: "0 0 8px 0",
    color: "#fff",
    textShadow: "2px 2px 4px rgba(0,0,0,0.3)"
  },
  guildCount: {
    fontSize: 16,
    color: "rgba(255,255,255,0.9)",
    fontWeight: 500
  },
  backButton: {
    background: "rgba(255,255,255,0.2)",
    color: "#fff",
    border: "2px solid rgba(255,255,255,0.3)",
    borderRadius: 12,
    padding: "12px 20px",
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.3s",
    backdropFilter: "blur(10px)"
  },
  searchContainer: {
    borderRadius: 20,
    padding: 24,
    marginBottom: 32,
    color: "#1f2937"
  },
  searchRow: {
    display: "flex",
    gap: 16,
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: 16
  },
  searchInputContainer: {
    position: "relative",
    flex: 1,
    minWidth: 250
  },
  searchInput: {
    width: "100%",
    padding: "14px 50px 14px 20px",
    border: "2px solid #e5e7eb",
    borderRadius: 12,
    fontSize: 16,
    background: "#fff",
    color: "#1f2937",
    transition: "all 0.3s",
    boxSizing: "border-box"
  },
  searchIcon: {
    position: "absolute",
    right: 16,
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: 18,
    color: "#6b7280"
  },
  sportFilter: {
    padding: "14px 16px",
    border: "2px solid #e5e7eb",
    borderRadius: 12,
    fontSize: 14,
    background: "#fff",
    color: "#1f2937",
    fontWeight: 600,
    minWidth: 120,
    cursor: "pointer"
  },
  sortSelect: {
    padding: "14px 16px",
    border: "2px solid #e5e7eb",
    borderRadius: 12,
    fontSize: 14,
    background: "#fff",
    color: "#1f2937",
    fontWeight: 600,
    minWidth: 130,
    cursor: "pointer"
  },
  activeFilters: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap"
  },
  filterTag: {
    background: "#fef3c7",
    color: "#d97706",
    padding: "6px 12px",
    borderRadius: 20,
    fontSize: 14,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: 8,
    border: "1px solid #fbbf24"
  },
  filterRemove: {
    background: "none",
    border: "none",
    color: "#d97706",
    cursor: "pointer",
    fontSize: 16,
    padding: 0,
    width: 16,
    height: 16,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700
  },
  sortInfo: {
    background: "#e5e7eb",
    color: "#374151",
    padding: "6px 12px",
    borderRadius: 20,
    fontSize: 14,
    fontWeight: 500,
    marginLeft: "auto"
  },
  guildGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
    gap: 24,
    marginBottom: 32
  },
  guildCard: {
    borderRadius: 20,
    padding: 24,
    transition: "all 0.3s",
    border: "1px solid rgba(0,0,0,0.1)",
    color: "#1f2937"
  },
  guildCardHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: 16,
    marginBottom: 16
  },
  guildCardAvatar: {
    flexShrink: 0
  },
  guildCardImage: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    objectFit: "cover",
    border: "3px solid #e5e7eb"
  },
  guildCardDefaultAvatar: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    backgroundColor: "#f3f4f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 28,
    border: "3px solid #e5e7eb",
    color: "#6b7280"
  },
  guildCardInfo: {
    flex: 1
  },
  guildCardName: {
    fontSize: 20,
    fontWeight: 800,
    margin: "0 0 8px 0",
    color: "#1f2937"
  },
  guildCardSport: {
    fontSize: 14,
    fontWeight: 600,
    color: "#7c3aed",
    marginBottom: 4,
    background: "#f3e8ff",
    padding: "2px 8px",
    borderRadius: 12,
    display: "inline-block"
  },
  guildCardLeader: {
    fontSize: 13,
    color: "#6b7280"
  },
  guildCardRank: {
    textAlign: "center",
    flexShrink: 0
  },
  rankNumber: {
    fontSize: 24,
    fontWeight: 900,
    color: "#f59e0b",
    lineHeight: 1
  },
  rankLabel: {
    fontSize: 11,
    color: "#6b7280",
    marginTop: 2
  },
  guildCardIntro: {
    fontSize: 14,
    color: "#6b7280",
    lineHeight: 1.5,
    marginBottom: 16,
    fontStyle: "italic"
  },
  guildCardStats: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 16,
    marginBottom: 20,
    padding: 16,
    background: "#f8fafc",
    borderRadius: 12,
    border: "1px solid #e5e7eb"
  },
  statItem: {
    textAlign: "center"
  },
  statIcon: {
    display: "block",
    fontSize: 16,
    marginBottom: 4
  },
  statValue: {
    display: "block",
    fontSize: 16,
    fontWeight: 700,
    color: "#1f2937",
    lineHeight: 1.2
  },
  statLabel: {
    display: "block",
    fontSize: 11,
    color: "#6b7280",
    marginTop: 2
  },
  guildCardFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  guildActivity: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    color: "#10b981",
    fontWeight: 600
  },
  activityDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "#10b981",
    animation: "pulse 2s infinite"
  },
  applyButton: {
    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    color: "#fff",
    border: "none",
    borderRadius: 12,
    padding: "12px 20px",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.3s",
    boxShadow: "0 2px 8px rgba(16,185,129,0.3)"
  },
  disabledButton: {
    opacity: 0.6,
    cursor: "not-allowed"
  },
  emptyState: {
    textAlign: "center",
    padding: 80,
    gridColumn: "1 / -1",
    color: "#fff"
  },
  emptyIcon: {
    fontSize: 64,
    marginBottom: 20,
    opacity: 0.7
  },
  emptyTitle: {
    fontSize: 24,
    fontWeight: 700,
    marginBottom: 8,
    color: "#fff"
  },
  emptySubtext: {
    fontSize: 16,
    opacity: 0.8,
    marginBottom: 24,
    lineHeight: 1.5
  },
  resetButton: {
    background: "rgba(255,255,255,0.2)",
    color: "#fff",
    border: "2px solid rgba(255,255,255,0.3)",
    borderRadius: 12,
    padding: "12px 24px",
    fontSize: 16,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s",
    backdropFilter: "blur(10px)"
  },
  createPrompt: {
    borderRadius: 20,
    padding: 24,
    display: "flex",
    alignItems: "center",
    gap: 20,
    flexWrap: "wrap",
    color: "#1f2937"
  },
  promptIcon: {
    fontSize: 32,
    flexShrink: 0
  },
  promptText: {
    flex: 1,
    fontSize: 16,
    lineHeight: 1.5
  },
  createPromptButton: {
    background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
    color: "#fff",
    border: "none",
    borderRadius: 12,
    padding: "14px 24px",
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.3s",
    flexShrink: 0,
    boxShadow: "0 2px 8px rgba(239,68,68,0.3)"
  }
};

export default GuildList;